<script setup lang="ts">
  import { computed } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { useHead, useRoute } from 'nuxt/app'

  import { provideSeoSiteProfile } from '@common/core-layer/composables/seo/useSeo'
  import { useLocation } from '@common/location-layer/composables/useLocation'

  import { isCPStandaloneShellRoutePath } from '@clientportal/routing-layer/constants/auth.constants'
  import { isCPAuthRoutePath } from '@clientportal/routing-layer/utils/auth-route.utils'

  import { createClientPortalSeoSiteProfile } from './seo/clientPortalSeoProfile'

  const CPRootContainer = defineAsyncComponent(() => import('./components/CPRootContainer.vue'))

  const route = useRoute()
  const { locale } = useI18n()
  const { store: locationStore } = useLocation()

  const documentLanguage = computed(() => {
    const normalizedLocale = locale.value.trim().replaceAll('_', '-')
    if (!normalizedLocale) return 'en'

    try {
      return Intl.getCanonicalLocales(normalizedLocale)[0] ?? 'en'
    } catch {
      return 'en'
    }
  })

  const isStandaloneCpShellRoute = computed(() => {
    const standaloneFromRouteMeta = Boolean(route.meta?.standaloneCpShell)
    const standaloneFromMatchedMeta =
      route.matched?.some(record => record.meta?.standaloneCpShell === true || record.meta?.standalonePublicRoute === true) ?? false
    const standaloneFromPath = isCPStandaloneShellRoutePath(route.path)
    return standaloneFromRouteMeta || standaloneFromMatchedMeta || standaloneFromPath
  })

  const layoutBoundaryKey = computed(() => (isCPAuthRoutePath(route.path) ? 'auth' : 'portal'))

  // Legal pages render outside CPRootContainer, so this provide is the one useSeo reads.
  // Capture the store here — Unhead can re-run the getter after the active Pinia is gone.
  provideSeoSiteProfile(() => createClientPortalSeoSiteProfile(locationStore))

  useHead(() => ({
    htmlAttrs: {
      lang: documentLanguage.value,
    },
  }))
</script>

<template>
  <NuxtPage v-if="isStandaloneCpShellRoute" />
  <CPRootContainer v-else>
    <NuxtLayout :key="layoutBoundaryKey">
      <NuxtPage />
    </NuxtLayout>
  </CPRootContainer>
</template>
